import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Flex, Text } from "rebass/styled-components";
import { useRecoilValue } from "recoil";
import styled from "styled-components";

import { analytics } from "MVVM/Analytics";
import { ESupportedGames } from "MVVM/@types";
import { MatchHistoryMatchThumbnailState } from "MVVM/State/matchHistory";
import { URLS } from "MVVM/Utilities/Maps";
import { DynamicSignupOptions } from "MVVM/Views/Authentication/Components/DynamicSignupOptions";
import { TMoment } from "MVVM/Views/MatchHistory/@types";
import { MomentDetails } from "../MomentDetails";

export const MatchHistoryLoginModal = ({
  game,
  moment,
}: {
  game: ESupportedGames;
  moment: TMoment;
}) => {
  const history = useHistory();
  const matchThumbnail = useRecoilValue(MatchHistoryMatchThumbnailState);
  const { t } = useTranslation();

  const getPlatform = () => {
    if (game === ESupportedGames.CS2) return "Steam";
    if (game === ESupportedGames.LOL) return "Riot";
    if (game === ESupportedGames.FORTNITE) return "Epic";
    return "";
  };

  return (
    <Container>
      <ChampionBanner
        style={{
          backgroundImage: `linear-gradient(to bottom, transparent, var(--Gray-750, #1c2a34)), url(${matchThumbnail})`,
        }}
      />
      <MomentDetails moment={moment} />
      <Flex
        style={{ padding: "12px 24px 24px", gap: "24px" }}
        flexDirection="column"
      >
        <Flex flexDirection="column" textAlign="center" style={{ gap: "12px" }}>
          <Title color="ice" variant="text.titleSmall">
            {t("matchHistoryLoginModal.title", "Don't Miss Out On Your Play!")}
          </Title>
          <Subtitle color="chalk" variant="text.paragraphMedium">
            {t(
              "matchHistoryLoginModal.subtitle",
              "Create a FREE Allstar.gg account to create, edit, and share a clip from this moment.",
            )}
          </Subtitle>
        </Flex>
        <DynamicSignupOptions game={game} renderCopy={false} />
        <Flex style={{ gap: 10 }} justifyContent="center">
          <Text color="chalk" variant={"text.labelMedium"}>
            {t("matchHistoryLoginModal.signupText", {
              platform: getPlatform(),
              defaultValue: `Don't want to login with ${getPlatform()}?`,
            })}
          </Text>
          <LoginText
            variant={"text.labelMedium"}
            color="envy"
            onClick={() => {
              analytics.track("Match History - Login Modal", {
                type: "signup",
                action: "click",
              });
              const nextLink = window.location.search
                ? `${window.location.pathname}${window.location.search}`
                : window.location.pathname;
              history.push(`${URLS.SIGNUP}?next=${nextLink}`);
            }}
          >
            {t("signup.signup", "Sign up")}
          </LoginText>
        </Flex>
      </Flex>
    </Container>
  );
};

const Container = styled(Flex)`
  position: relative;
  width: 448px;
  flex-direction: column;
  background-color: var(--Gray-750, #1c2a34);
  text-align: center;
  border-radius: 14px;
  border: ${({ theme }) => `1px solid ${theme.colors.envyAlpha20}`};
  box-shadow: ${({ theme }) => `0px 0px 16px 0px ${theme.colors.envyAlpha20}`};

  @media (max-width: 500px) {
    width: 390px;
  }

  @media (max-width: 425px) {
    width: 100%;
  }
`;

const ChampionBanner = styled(Flex)`
  background-size: cover;
  height: 300px;
  width: 100%;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
`;

const LoginText = styled(Text)`
  cursor: pointer;
  margin-right: 15px;

  @media (max-width: 425px) {
    margin-right: 0px;
  }
`;

const Title = styled(Text)`
  @media (max-width: 425px) {
    font-size: 24px;
    line-height: 28px;
  }
`;

const Subtitle = styled(Text)`
  @media (max-width: 425px) {
    font-size: 14px;
    line-height: 20px;
  }
`;
